<template>
  <div class="invoice-view" v-if="invoice">
    <div class="invoice-toolbar">
      <button class="export-btn" :disabled="exportingPdf" @click="exportPdf">
        {{ exportingPdf ? $t('buy.invoice.exportingPdf') : $t('buy.invoice.exportPdf') }}
      </button>

    </div>

    <div
      class="invoice-content-layout"
      :class="{ 'invoice-content-layout--with-image': isUploadedInvoiceImage }"
    >
    <div class="invoice-page">
      <div class="top-grid">
        <div class="logo-company">
          <div class="logo-wrap">
            <img class="mt-logo" :src="logoSrc" :alt="$t('brand.logoAlt')" />
          </div>
          <div class="sender-box">
            <p>{{ invoiceBrand.supplierName }}</p>
            <p v-for="line in invoiceBrand.addressLines" :key="line">{{ line }}</p>
            <p>{{ invoiceBrand.email }}</p>

            <!-- <p>Tax-ID-No.: {{ invoiceBrand.taxId }}</p> -->
          </div>
        </div>

        <div class="invoice-meta">
          <div class="meta-table">
            <div class="meta-row">
              <div class="meta-cell left-head">{{ $t('buy.invoice.labels.documentTitle') }}</div>
              <div class="meta-cell right-head">{{ invoiceNumber }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.issueDate') }}:</div>
              <div class="meta-cell value-cell">{{ issueDate }}</div>
            </div>
            <div class="meta-row blank-row">
              <div class="meta-cell meta-cell--full"></div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.clientNo') }}:</div>
              <div class="meta-cell value-cell">{{ clientNo }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.organization') }}:</div>
              <div class="meta-cell value-cell">{{ organization }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.address') }}:</div>
              <div class="meta-cell value-cell">{{ address }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.name') }}:</div>
              <div class="meta-cell value-cell">{{ contactName }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.email') }}:</div>
              <div class="meta-cell value-cell">{{ email }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.phone') }}:</div>
              <div class="meta-cell value-cell">{{ phone }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.taxId') }}:</div>
              <div class="meta-cell value-cell">{{ taxId }}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell label-cell">{{ $t('buy.invoice.labels.registrationNo') }}:</div>
              <div class="meta-cell value-cell">{{ registrationNumber }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="line-table">
        <div class="line-row head-row">
          <div class="line-cell matter-col">{{ $t('buy.invoice.labels.matter') }}:</div>
          <div class="line-cell line-cell--period">{{ $t('buy.invoice.labels.period') }}:</div>
          <div class="line-cell currency-col">{{ $t('buy.invoice.labels.currency') }}:</div>
          <div class="line-cell amount-col">{{ $t('buy.invoice.labels.amount') }}:</div>
        </div>
        <div v-for="(item, index) in invoiceItems" :key="index" class="line-row">
          <div class="line-cell">{{ item.matter }}</div>
          <div class="line-cell">{{ periodStart }}</div>
          <div class="line-cell">{{ periodEnd }}</div>
          <div class="line-cell center">{{ currency }}</div>
          <div class="line-cell right">{{ formatAmount(item.amount) }}</div>
        </div>
        <div class="line-row">
          <div class="line-cell line-cell--summary-spacer"></div>
          <div class="line-cell line-cell--summary-label summary-label strong">{{ $t('buy.invoice.labels.subtotal') }}:</div>
          <div class="line-cell center">{{ currency }}</div>
          <div class="line-cell right">{{ netAmountText }}</div>
        </div>
        <div class="line-row">
          <div class="line-cell line-cell--summary-label summary-label strong">{{ $t('buy.invoice.labels.discount') }}:</div>
          <div class="line-cell center">{{ currency }}</div>
          <div class="line-cell right">{{ discountAmountText }}</div>
        </div>
        <div class="line-row">
          <div class="line-cell line-cell--summary-label summary-label strong">{{ taxLabel }}:</div>
          <div class="line-cell center">{{ isMtBrand ? currency : '' }}</div>
          <div class="line-cell right">{{ isMtBrand ? taxAmountText : '' }}</div>
        </div>
        <div class="line-row line-row--last">
          <div class="line-cell line-cell--summary-label summary-label strong">{{ $t('buy.invoice.labels.billingAmount') }}:</div>
          <div class="line-cell center">{{ currency }}</div>
          <div class="line-cell right strong">{{ billingAmountText }}</div>
        </div>
      </div>

      <div class="info-block">
        <div class="block-title">{{ $t('buy.invoice.labels.customerService') }}:</div>
        <div class="block-content">
          {{ $t('buy.invoice.labels.customerServiceText') }}
        </div>
      </div>

      <div class="info-block">
        <div class="block-title">{{ $t('buy.invoice.labels.paymentInstructions') }}:</div>
        <div class="block-content">
          <p>
            {{ $t('buy.invoice.labels.paymentText') }}
          </p>
          <p class="strong">
            {{ $t('buy.invoice.labels.paymentNote') }}
          </p>
        </div>
      </div>

      <div v-if="$brand && $brand.id === 'mt'" class="info-block bank-title-only">
        <div class="block-title">{{ $t('buy.invoice.labels.bankInformation') }}:</div>
      </div>
      <div v-if="$brand && $brand.id === 'mt'" class="block-content">
        <p v-for="line in invoiceBrand.bankLines" :key="line">{{ line }}</p>
      </div>

      <footer v-if="isMtBrand" class="invoice-payment-footer">
        <div class="invoice-payment-qr">
          <img
            :src="mtPaymentQrSrc"
            :alt="$t('buy.invoice.paymentQr.alt')"
            width="244"
            height="244"
          />
          <div class="invoice-payment-details">
            <h2>{{ $t('buy.invoice.paymentQr.title') }}</h2>
            <p>{{ mtPaymentPayload }}</p>
          </div>
        </div>
      </footer>

    </div>
      <aside v-if="isUploadedInvoiceImage" class="uploaded-invoice-preview">
        <button
          type="button"
          class="uploaded-invoice-download"
          :disabled="downloadingUploadedInvoice"
          :aria-label="$t('buy.invoice.downloadUploadedImage')"
          @click="downloadUploadedInvoice"
        >
          <img
            class="uploaded-invoice-image"
            :src="uploadedInvoiceUrl"
            :alt="uploadedInvoiceName || invoiceNumber"
            @error="uploadedInvoiceImageFailed = true"
          />
          <span class="uploaded-invoice-meta">
            <strong>{{ uploadedInvoiceName || invoiceNumber }}</strong>
            <span>
              <i class="fas fa-download" aria-hidden="true"></i>
              {{ $t('buy.invoice.downloadUploadedImage') }}
            </span>
          </span>
        </button>
      </aside>
    </div>

    <!-- <CallMe /> -->
  </div>
  <div v-else class="invoice-loading">
    <i class="el-icon-loading"></i>
    <span>{{ $t('common.loading') }}</span>
  </div>
</template>

<script>
import CallMe from '@/components/callme/index'
import request from '@/utils/http'
import { paymentAdminApi } from '@/api/paymentAdmin'
import { userApi } from '@/api/user'
import { brandPrefix } from '@/branding'
import { formatPrice } from '@/utils/currency'
import mtPaymentQrSrc from '@/assets/img/mt-invoice-payment-qr.svg'
import { MT_INVOICE_PAYMENT_PAYLOAD } from '@/config/mtInvoicePayment'

const PLAN_LABELS = {
  starter: 'Starter Package',
  professional: 'Professional Package',
  enterprise: 'Enterprise Package'
}

const STANDARD_LABELS = {
  iso9001: 'ISO 9001',
  iatf16949: 'IATF 16949',
  vda63: 'VDA 6.3',
  vda65: 'VDA 6.5',
  iso14001: 'ISO 14001',
  iso140012026: 'ISO 14001:2026',
  tisax: 'TISAX',
  iso13485: 'ISO 13485',
  iso45001: 'ISO 45001',
  iso50001: 'ISO 50001',
  iso27001: 'ISO 27001',
  customTemplate: 'Custom Template'
}

const XRECHNUNG_CUSTOMIZATION_ID = 'urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0'
const PEPPOL_BILLING_PROFILE_ID = 'urn:fdc:peppol.eu:2017:poacc:billing:01:1.0'

export default {
  name: 'InvoicePage',
  components: {
    CallMe
  },
  data() {
    return {
      invoice: null,
      order: null,
      uploadedInvoiceFile: null,
      uploadedInvoiceImageFailed: false,
      downloadingUploadedInvoice: false,
      exportingPdf: false,
      exportingXml: false
    }
  },
  computed: {
    mtPaymentQrSrc() {
      return mtPaymentQrSrc
    },
    mtPaymentPayload() {
      return MT_INVOICE_PAYMENT_PAYLOAD
    },
    invoiceBrand() {
      const configured = (this.$brand && this.$brand.invoice) || {}
      const locale = (this.$i18n && this.$i18n.locale) || 'en'
      const language = locale === 'zh' || locale === 'zhCN' ? 'zhCN' : locale
      const localized = (configured.messages && (configured.messages[language] || configured.messages.en)) || {}
      return { ...configured, ...localized, ...(!configured.messages && {
        numberPrefix: brandPrefix(),
        supplierName: this.$t('company.fullName'),
        addressLines: [],
        email: '',
        registrationNumber: '',
        taxId: '',
        bankLines: []
      }) }
    },
    logoSrc() {
      return (this.$brand && (this.$brand.logoForFapiao || this.$brand.logoforfapiao)) || '/brands/lpt/logo.png'
    },
    buyer() {
      return (this.invoice && this.invoice.buyer) || {}
    },
    organizationInfo() {
      return (this.invoice && this.invoice.organization) || {}
    },
    organizationAddress() {
      return this.organizationInfo.address || {}
    },
    subtotalAmount() {
      if (this.order && this.order.subtotalAmount != null) {
        return Number(this.order.subtotalAmount) / 100
      }
      return 0
    },
    netAmountText() {
      return this.formatAmount(this.subtotalAmount)
    },
    discountAmount() {
      if (this.order && this.order.discountAmount != null) {
        return Number(this.order.discountAmount) / 100
      }
      return 0
    },
    discountAmountText() {
      return this.formatAmount(this.discountAmount)
    },
    taxAmount() {
      if (this.isMtBrand) {
        const taxable = Math.max(this.subtotalAmount - this.discountAmount, 0)
        return taxable * 0.081
      }
      return 0
    },
    taxAmountText() {
      return this.formatAmount(this.taxAmount)
    },
    isMtBrand() {
      return this.$brand && this.$brand.id === 'mt'
    },
    taxLabel() {
      return this.isMtBrand
        ? this.$t('buy.invoice.taxWithRate', { rate: '8.1%' })
        : this.$t('buy.invoice.tax')
    },
    billingAmount() {
      const taxable = Math.max(this.subtotalAmount - this.discountAmount, 0)
      return taxable + this.taxAmount
    },
    billingAmountText() {
      return this.formatAmount(this.billingAmount)
    },
    totalAmount() {
      if (this.order && this.order.finalAmount != null) {
        return Number(this.order.finalAmount) / 100
      }
      const amount = Number(this.$route.query.amount || 0)
      return Number.isFinite(amount) ? amount : 0
    },
    invoiceNumber() {
      if (this.order && this.order.orderNo) {
        return this.order.orderNo.replace('ORD', brandPrefix())
      }
      const fallbackId = (this.$route.query.orderId || `${brandPrefix()}-202X-XX-XX`).toString()
      return fallbackId
    },
    issueDate() {
      if (this.order && (this.order.updatedAt || this.order.createdAt)) {
        const date = new Date(this.order.updatedAt || this.order.createdAt)
        if (!Number.isNaN(date.getTime())) {
          return this.formatDate(date)
        }
      }
      const raw = this.$route.query.paymentTime
      if (!raw) return 'XX-XX-202X'
      const date = new Date(raw)
      if (Number.isNaN(date.getTime())) return 'XX-XX-202X'
      return this.formatDate(date)
    },
    periodStart() {
      return this.issueDate
    },
    periodEnd() {
      if (this.issueDate === 'XX-XX-202X') return 'XX-XX-202X'
      const baseDate = this.order && (this.order.updatedAt || this.order.createdAt)
        ? new Date(this.order.updatedAt || this.order.createdAt)
        : null
      if (!baseDate || Number.isNaN(baseDate.getTime())) return 'XX-XX-202X'
      const periodEnd = new Date(baseDate.getTime())
      const years = this.order && String(this.order.billingCycle).toLowerCase() === 'two_year' ? 2 : 1
      periodEnd.setFullYear(periodEnd.getFullYear() + years)
      return this.formatDate(periodEnd)
    },
    clientNo() {
      if (this.invoice && this.invoice.clientNumber) {
        return this.invoice.clientNumber.replace('ORD', brandPrefix())
      }
      const id = (this.$route.query.orderId || '').toString()
      if (id) return id
      return ''
    },
    organization() {
      return this.organizationInfo.name || this.organizationInfo.legalName || ''
    },
    address() {
      const addr = this.organizationAddress
      if (!addr || !Object.keys(addr).length) return ''
      return [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.stateOrProvince,
        addr.postalCode,
        addr.countryOrRegion
      ].filter(Boolean).join(', ')
    },
    contactName() {
      const fullName = this.buyer.fullName || {}
      return [fullName.firstName, fullName.lastName].filter(Boolean).join(' ').trim()
    },
    email() {
      return this.buyer.email || ''
    },
    phone() {
      return this.organizationInfo.phoneNumber || ''
    },
    taxId() {
      return this.organizationInfo.taxIdentificationNumber || ''
    },
    registrationNumber() {
      return this.organizationInfo.registrationNumber || ''
    },
    currency() {
      return (this.order && this.order.displayCurrency) || 'RMB'
    },
    invoiceItems() {
      const order = this.order || {}
      if (Array.isArray(order.invoiceItems) && order.invoiceItems.length) {
        return order.invoiceItems.map(item => ({
          matter: item.matter,
          amount: Number(item.amount || 0) / 100
        }))
      }
      const snapshot = order.purchaseSnapshot || {}
      const items = []
      const plan = snapshot.selectedPlan || {}
      const languages = snapshot.selectedLanguages || {}
      const standards = snapshot.selectedStandards || {}
      const seats = snapshot.seatsConfig || {}
      const cycleMultiplier = this.order && String(this.order.billingCycle).toLowerCase() === 'two_year' ? 2 : 1

      const planAmount = Number(plan.price || 0)
      if (planAmount > 0) {
        items.push({
          matter: PLAN_LABELS[plan.planId || order.planId] || `Plan: ${plan.planId || order.planId || 'Package'}`,
          amount: planAmount
        })
      }

      const selectedLanguages = Array.isArray(order.languages) && order.languages.length
        ? order.languages
        : (Array.isArray(languages.languages) ? languages.languages : [])
      if (selectedLanguages.length > 0) {
        items.push({
          matter: this.$t('buy.invoice.languagesLine', {
            languages: selectedLanguages.map(this.languageLabel).join(', ')
          }),
          amount: 0
        })
      }

      if (Array.isArray(order.standards) && order.standards.length > 0) {
        const standardCount = order.standards.length
        const standardTotal = this.resolveCycleAmount(standards.totalPrice)
        const fallbackStandardAmount = standardCount > 0 ? standardTotal / standardCount : 0
        order.standards.forEach(standardId => {
          items.push({
            matter: `Audit Template: ${this.standardLabel(standardId)}`,
            amount: fallbackStandardAmount
          })
        })
      }

      const seatsAmount = this.resolveCycleAmount(seats.totalPrice)
      const extraAdminTotal = 60 * Number(order.extraAdminSeats || 0) * cycleMultiplier
      if (Number(order.auditorSeats || 0) > 0) {
        items.push({
          matter: `Auditor licences: ${order.auditorSeats}`,
          amount: Math.max(seatsAmount - extraAdminTotal, 0)
        })
      }

      if (Number(order.extraAdminSeats || 0) > 0) {
        items.push({
          matter: `Extra Admin licences: ${order.extraAdminSeats}`,
          amount: extraAdminTotal
        })
      }

      if (!items.length && order.finalAmount != null) {
        items.push({
          matter: `Order ${order.orderNo || ''}`.trim(),
          amount: Number(order.finalAmount) / 100
        })
      }

      return items
    },
    uploadedInvoiceUrl() {
      return (this.uploadedInvoiceFile && this.uploadedInvoiceFile.url) ||
        (this.order && this.order.invoiceFileUrl) || ''
    },
    uploadedInvoiceName() {
      return (this.uploadedInvoiceFile && this.uploadedInvoiceFile.filename) ||
        (this.order && this.order.invoiceFileName) || ''
    },
    uploadedInvoiceContentType() {
      return (this.uploadedInvoiceFile &&
        this.uploadedInvoiceFile.attributes &&
        this.uploadedInvoiceFile.attributes.contentType) || ''
    },
    isUploadedInvoiceImage() {
      if (!this.uploadedInvoiceUrl || this.uploadedInvoiceImageFailed) return false
      if (this.uploadedInvoiceContentType.startsWith('image/')) return true
      return /\.(png|jpe?g)(?:$|[?#])/i.test(this.uploadedInvoiceName)
    },
    buyerReference() {
      const order = this.order || {}
      return order.buyerReference || order.leitwegId || order.companyBuyerReference || this.clientNo || this.invoiceNumber
    }
  },
  async created() {
    await this.loadOrder()
  },
  methods: {
    async loadOrder() {
      const orderNo = this.$route.query.orderNo || this.$route.query.orderId
      if (!orderNo) return
      try {
        this.invoice = await request.get(`/api/invoices/${String(orderNo).replace(/^LPT-/, '')}`)
        this.order = (this.invoice && this.invoice.order) || null
        await this.loadUploadedInvoice()
      } catch (e) {
        console.error('Failed to load invoice order', e)
      }
      if (!this.invoice) {
        this.applyMockInvoice(orderNo)
      }
    },
    applyMockInvoice(orderNo) {
      const brandId = (this.$brand && this.$brand.id) || 'lpt'
      const currency = brandId === 'mt' ? 'EUR' : 'RMB'
      const now = new Date().toISOString()
      const order = {
        brandCode: brandId,
        orderNo: orderNo,
        status: 'PAID',
        planId: 'professional',
        billingCycle: 'one_year',
        paymentMode: 'one_time',
        paymentMethod: 'card',
        region: brandId === 'mt' ? 'DE' : 'CN',
        displayCurrency: currency,
        paymentCurrency: brandId === 'mt' ? 'EUR' : 'CNY',
        subtotalAmount: 313000,
        discountAmount: 31300,
        finalAmount: 281700,
        languages: ['english', 'french'],
        standards: ['iso9001'],
        auditorSeats: 3,
        extraAdminSeats: 1,
        invoiceItems: [
          { matter: 'Professional Package', amount: 264000 },
          { matter: 'Audit Template: ISO 9001', amount: 40000 },
          { matter: 'Auditor licences: 3', amount: 9000 }
        ],
        createdAt: now,
        updatedAt: now
      }
      this.invoice = {
        clientNumber: orderNo,
        buyer: {
          fullName: { firstName: 'Max', lastName: 'Mustermann' },
          email: 'max.mustermann@example.com'
        },
        organization: {
          name: 'Mustermann GmbH',
          legalName: 'Mustermann GmbH',
          phoneNumber: '+49 711 1234567',
          taxIdentificationNumber: 'DE123456789',
          registrationNumber: 'HRB 123456',
          address: {
            addressLine1: 'Hauptstraße 12',
            addressLine2: '',
            city: 'Stuttgart',
            stateOrProvince: 'Baden-Württemberg',
            postalCode: '70173',
            countryOrRegion: 'Germany'
          }
        },
        order: order
      }
      this.order = order
      console.warn('[Invoice] Using mock invoice data for', orderNo)
    },
    async loadUploadedInvoice() {
      const fileId = this.order && this.order.invoiceFileId
      if (!fileId) return
      try {
        this.uploadedInvoiceFile = await paymentAdminApi.getInvoiceFile(fileId)
        this.uploadedInvoiceImageFailed = false
      } catch (e) {
        console.warn('Failed to refresh uploaded invoice URL', e)
      }
    },
    async downloadUploadedInvoice() {
      if (this.downloadingUploadedInvoice) return
      this.downloadingUploadedInvoice = true
      try {
        const fileId = this.order && this.order.invoiceFileId
        const file = fileId
          ? await paymentAdminApi.getInvoiceFile(fileId, { download: true })
          : this.uploadedInvoiceFile
        const downloadUrl = (file && file.url) || this.uploadedInvoiceUrl
        if (!downloadUrl) throw new Error('Missing uploaded invoice URL')

        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = (file && file.filename) || this.uploadedInvoiceName || 'invoice'
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        console.error('Failed to download uploaded invoice', e)
        this.showMessage('error', this.$t('buy.invoice.downloadUploadedImageFailed'))
      } finally {
        this.downloadingUploadedInvoice = false
      }
    },
    formatDate(date) {
      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`
    },
    formatAmount(amount) {
      return formatPrice(amount)
    },
    formatXmlAmount(amount) {
      const normalized = Number(amount || 0)
      return Number.isFinite(normalized) ? normalized.toFixed(2) : '0.00'
    },
    issueDateIso() {
      return this.toIsoDate(this.issueDate)
    },
    periodStartIso() {
      return this.toIsoDate(this.periodStart)
    },
    periodEndIso() {
      return this.toIsoDate(this.periodEnd)
    },
    toIsoDate(value) {
      if (!value || value === 'XX-XX-202X') return ''
      const dateText = String(value).trim()
      const match = dateText.match(/^(\d{2})-(\d{2})-(\d{4})$/)
      if (match) {
        return `${match[3]}-${match[2]}-${match[1]}`
      }
      const date = new Date(dateText)
      if (Number.isNaN(date.getTime())) return ''
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    resolveCycleAmount(baseAmount) {
      const normalized = Number(baseAmount || 0)
      if (!normalized) return 0
      return this.order && String(this.order.billingCycle).toLowerCase() === 'two_year'
        ? normalized * 2
        : normalized
    },
    standardLabel(id) {
      return STANDARD_LABELS[id] || id
    },
    languageLabel(id) {
      const label = this.$t(`buy.languages.items.${id}`)
      return label === `buy.languages.items.${id}` ? id : label
    },
    async exportXml() {
      if (this.exportingXml) return
      this.exportingXml = true

      try {
        const missingFields = this.validateXmlExportFields()
        if (missingFields.length) {
          this.showMessage('error', this.$t('buy.invoice.xmlExportMissingFields', {
            fields: missingFields.join(', ')
          }))
          return
        }

        const xml = this.buildXrechnungUblXml()
        const safeInvoiceNo = this.invoiceNumber.replace(/[^a-zA-Z0-9-_]/g, '_')
        this.downloadTextFile(xml, `Invoice-${safeInvoiceNo}.xml`, 'application/xml;charset=utf-8')
        this.showMessage('success', this.$t('buy.invoice.xmlExportSuccess'))
      } catch (error) {
        console.error('Failed to export invoice XML:', error)
        this.showMessage('error', this.$t('buy.invoice.xmlExportFailed'))
      } finally {
        this.exportingXml = false
      }
    },
    validateXmlExportFields() {
      const fieldMap = [
        { value: this.invoiceNumber, label: this.$t('buy.invoice.xmlFields.invoiceNumber') },
        { value: this.issueDateIso(), label: this.$t('buy.invoice.xmlFields.issueDate') },
        { value: this.currency, label: this.$t('buy.invoice.xmlFields.currency') },
        { value: this.invoiceBrand.supplierName, label: this.$t('buy.invoice.xmlFields.supplierName') },
        { value: this.organization, label: this.$t('buy.invoice.xmlFields.customerOrganization') },
        { value: this.address, label: this.$t('buy.invoice.xmlFields.customerAddress') },
        { value: this.invoiceItems.length > 0, label: this.$t('buy.invoice.xmlFields.invoiceItems') },
        { value: Number.isFinite(Number(this.totalAmount)) && Number(this.totalAmount) >= 0, label: this.$t('buy.invoice.xmlFields.totalAmount') }
      ]
      return fieldMap
        .filter(item => !item.value)
        .map(item => item.label)
    },
    buildXrechnungUblXml() {
      const lineExtensionAmount = this.invoiceItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)
      const subtotal = this.subtotalAmount || lineExtensionAmount
      const discount = this.discountAmount
      const taxExclusiveAmount = Math.max(subtotal - discount, 0)
      const payableAmount = this.totalAmount
      const customerEmail = this.email || 'no-reply@example.com'

      const invoiceLines = this.invoiceItems.map((item, index) => {
        const amount = Number(item.amount || 0)
        return [
          '  <cac:InvoiceLine>',
          `    <cbc:ID>${index + 1}</cbc:ID>`,
          '    <cbc:InvoicedQuantity unitCode="C62">1</cbc:InvoicedQuantity>',
          `    <cbc:LineExtensionAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(amount)}</cbc:LineExtensionAmount>`,
          '    <cac:Item>',
          `      <cbc:Name>${this.xml(item.matter || `Item ${index + 1}`)}</cbc:Name>`,
          '      <cac:ClassifiedTaxCategory>',
          '        <cbc:ID>O</cbc:ID>',
          '        <cbc:Percent>0.00</cbc:Percent>',
          '        <cac:TaxScheme>',
          '          <cbc:ID>VAT</cbc:ID>',
          '        </cac:TaxScheme>',
          '      </cac:ClassifiedTaxCategory>',
          '    </cac:Item>',
          '    <cac:Price>',
          `      <cbc:PriceAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(amount)}</cbc:PriceAmount>`,
          '    </cac:Price>',
          '  </cac:InvoiceLine>'
        ].join('\n')
      }).join('\n')

      const allowanceCharge = discount > 0
        ? [
          '  <cac:AllowanceCharge>',
          '    <cbc:ChargeIndicator>false</cbc:ChargeIndicator>',
          `    <cbc:AllowanceChargeReason>${this.xml(this.$t('buy.invoice.labels.discount'))}</cbc:AllowanceChargeReason>`,
          `    <cbc:Amount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(discount)}</cbc:Amount>`,
          '    <cac:TaxCategory>',
          '      <cbc:ID>O</cbc:ID>',
          '      <cbc:Percent>0.00</cbc:Percent>',
          '      <cac:TaxScheme>',
          '        <cbc:ID>VAT</cbc:ID>',
          '      </cac:TaxScheme>',
          '    </cac:TaxCategory>',
          '  </cac:AllowanceCharge>'
        ].join('\n')
        : ''

      return [
        '<?xml version="1.0" encoding="UTF-8"?>',
        '<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"',
        '         xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"',
        '         xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">',
        `  <cbc:CustomizationID>${this.xml(XRECHNUNG_CUSTOMIZATION_ID)}</cbc:CustomizationID>`,
        `  <cbc:ProfileID>${this.xml(PEPPOL_BILLING_PROFILE_ID)}</cbc:ProfileID>`,
        `  <cbc:ID>${this.xml(this.invoiceNumber)}</cbc:ID>`,
        `  <cbc:IssueDate>${this.xml(this.issueDateIso())}</cbc:IssueDate>`,
        '  <cbc:InvoiceTypeCode>380</cbc:InvoiceTypeCode>',
        `  <cbc:DocumentCurrencyCode>${this.xml(this.currency)}</cbc:DocumentCurrencyCode>`,
        `  <cbc:BuyerReference>${this.xml(this.buyerReference)}</cbc:BuyerReference>`,
        this.periodStartIso() && this.periodEndIso()
          ? [
            '  <cac:InvoicePeriod>',
            `    <cbc:StartDate>${this.xml(this.periodStartIso())}</cbc:StartDate>`,
            `    <cbc:EndDate>${this.xml(this.periodEndIso())}</cbc:EndDate>`,
            '  </cac:InvoicePeriod>'
          ].join('\n')
          : '',
        '  <cac:AccountingSupplierParty>',
        '    <cac:Party>',
        `      <cbc:EndpointID schemeID="EM">${this.xml(this.invoiceBrand.email)}</cbc:EndpointID>`,
        '      <cac:PartyName>',
        `        <cbc:Name>${this.xml(this.invoiceBrand.supplierName)}</cbc:Name>`,
        '      </cac:PartyName>',
        '      <cac:PostalAddress>',
        '        <cbc:StreetName>Kirchstraße 31</cbc:StreetName>',
        `        <cbc:CityName>${this.xml(this.invoiceBrand.addressLines[1] || '')}</cbc:CityName>`,
        '        <cbc:PostalZone></cbc:PostalZone>',
        '        <cac:Country>',
        '          <cbc:IdentificationCode>DE</cbc:IdentificationCode>',
        '        </cac:Country>',
        '      </cac:PostalAddress>',
        '      <cac:PartyTaxScheme>',
        `        <cbc:CompanyID>${this.xml(this.invoiceBrand.taxId)}</cbc:CompanyID>`,
        '        <cac:TaxScheme>',
        '          <cbc:ID>VAT</cbc:ID>',
        '        </cac:TaxScheme>',
        '      </cac:PartyTaxScheme>',
        '      <cac:PartyLegalEntity>',
        `        <cbc:RegistrationName>${this.xml(this.invoiceBrand.supplierName)}</cbc:RegistrationName>`,
        `        <cbc:CompanyID>${this.xml(this.invoiceBrand.registrationNumber)}</cbc:CompanyID>`,
        '      </cac:PartyLegalEntity>',
        '      <cac:Contact>',
        `        <cbc:ElectronicMail>${this.xml(this.invoiceBrand.email)}</cbc:ElectronicMail>`,
        '      </cac:Contact>',
        '    </cac:Party>',
        '  </cac:AccountingSupplierParty>',
        '  <cac:AccountingCustomerParty>',
        '    <cac:Party>',
        `      <cbc:EndpointID schemeID="EM">${this.xml(customerEmail)}</cbc:EndpointID>`,
        '      <cac:PartyName>',
        `        <cbc:Name>${this.xml(this.organization)}</cbc:Name>`,
        '      </cac:PartyName>',
        '      <cac:PostalAddress>',
        `        <cbc:StreetName>${this.xml(this.address)}</cbc:StreetName>`,
        `        <cbc:CityName>${this.xml(this.resolveCustomerCityName())}</cbc:CityName>`,
        `        <cbc:PostalZone>${this.xml(this.resolveCustomerPostalZone())}</cbc:PostalZone>`,
        '        <cac:Country>',
        `          <cbc:IdentificationCode>${this.xml(this.resolveCustomerCountryCode())}</cbc:IdentificationCode>`,
        '        </cac:Country>',
        '      </cac:PostalAddress>',
        this.taxId
          ? [
            '      <cac:PartyTaxScheme>',
            `        <cbc:CompanyID>${this.xml(this.taxId)}</cbc:CompanyID>`,
            '        <cac:TaxScheme>',
            '          <cbc:ID>VAT</cbc:ID>',
            '        </cac:TaxScheme>',
            '      </cac:PartyTaxScheme>'
          ].join('\n')
          : '',
        '      <cac:PartyLegalEntity>',
        `        <cbc:RegistrationName>${this.xml(this.organization)}</cbc:RegistrationName>`,
        this.registrationNumber ? `        <cbc:CompanyID>${this.xml(this.registrationNumber)}</cbc:CompanyID>` : '',
        '      </cac:PartyLegalEntity>',
        '      <cac:Contact>',
        this.contactName ? `        <cbc:Name>${this.xml(this.contactName)}</cbc:Name>` : '',
        customerEmail ? `        <cbc:ElectronicMail>${this.xml(customerEmail)}</cbc:ElectronicMail>` : '',
        '      </cac:Contact>',
        '    </cac:Party>',
        '  </cac:AccountingCustomerParty>',
        '  <cac:PaymentMeans>',
        '    <cbc:PaymentMeansCode>30</cbc:PaymentMeansCode>',
        '    <cac:PayeeFinancialAccount>',
        '      <cbc:ID>DE10611913100849609003</cbc:ID>',
        `      <cbc:Name>${this.xml(this.invoiceBrand.supplierName)}</cbc:Name>`,
        '      <cac:FinancialInstitutionBranch>',
        '        <cbc:ID>GENODES1VBP</cbc:ID>',
        '      </cac:FinancialInstitutionBranch>',
        '    </cac:PayeeFinancialAccount>',
        '  </cac:PaymentMeans>',
        allowanceCharge,
        '  <cac:TaxTotal>',
        `    <cbc:TaxAmount currencyID="${this.xml(this.currency)}">0.00</cbc:TaxAmount>`,
        '    <cac:TaxSubtotal>',
        `      <cbc:TaxableAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(taxExclusiveAmount)}</cbc:TaxableAmount>`,
        `      <cbc:TaxAmount currencyID="${this.xml(this.currency)}">0.00</cbc:TaxAmount>`,
        '      <cac:TaxCategory>',
        '        <cbc:ID>O</cbc:ID>',
        '        <cbc:Percent>0.00</cbc:Percent>',
        '        <cac:TaxScheme>',
        '          <cbc:ID>VAT</cbc:ID>',
        '        </cac:TaxScheme>',
        '      </cac:TaxCategory>',
        '    </cac:TaxSubtotal>',
        '  </cac:TaxTotal>',
        '  <cac:LegalMonetaryTotal>',
        `    <cbc:LineExtensionAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(subtotal)}</cbc:LineExtensionAmount>`,
        `    <cbc:TaxExclusiveAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(taxExclusiveAmount)}</cbc:TaxExclusiveAmount>`,
        `    <cbc:TaxInclusiveAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(payableAmount)}</cbc:TaxInclusiveAmount>`,
        discount > 0 ? `    <cbc:AllowanceTotalAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(discount)}</cbc:AllowanceTotalAmount>` : '',
        `    <cbc:PayableAmount currencyID="${this.xml(this.currency)}">${this.formatXmlAmount(payableAmount)}</cbc:PayableAmount>`,
        '  </cac:LegalMonetaryTotal>',
        invoiceLines,
        '</Invoice>'
      ].filter(line => line !== '').join('\n')
    },
    resolveCustomerCityName() {
      return this.organizationAddress.city || this.extractAddressPart(this.address, 1) || '-'
    },
    resolveCustomerPostalZone() {
      return this.organizationAddress.postalCode || this.extractPostalCode(this.address) || '-'
    },
    resolveCustomerCountryCode() {
      const country = (this.organizationAddress.countryOrRegion || '').toString().toUpperCase()
      if (country.includes('GERMANY') || country.includes('DEUTSCHLAND')) return 'DE'
      const codeMatch = country.match(/\b[A-Z]{2}\b/)
      return codeMatch ? codeMatch[0] : 'DE'
    },
    extractAddressPart(address, index) {
      return String(address || '').split(',').map(part => part.trim()).filter(Boolean)[index] || ''
    },
    extractPostalCode(address) {
      const match = String(address || '').match(/\b\d{4,10}\b/)
      return match ? match[0] : ''
    },
    xml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
    },
    downloadTextFile(content, filename, mimeType) {
      const blob = new Blob([content], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    showMessage(type, message) {
      if (this.$message && this.$message[type]) {
        this.$message[type](message)
        return
      }
      window.alert(message)
    },
    async exportPdf() {
      if (this.exportingPdf) return
      this.exportingPdf = true

      try {
        const [{ jsPDF }, { default: html2canvas }] = await Promise.all([
          import('jspdf'),
          import('html2canvas')
        ])
        const pages = Array.from(this.$el.querySelectorAll('.invoice-page'))
        const pdf = new jsPDF('p', 'pt', 'a4')
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = pdf.internal.pageSize.getHeight()

        for (let i = 0; i < pages.length; i++) {
          const page = pages[i]
          const canvas = await html2canvas(page, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
          })
          const imageData = canvas.toDataURL('image/png')
          const ratio = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height)
          const drawWidth = canvas.width * ratio
          const drawHeight = canvas.height * ratio
          const x = (pdfWidth - drawWidth) / 2
          const y = (pdfHeight - drawHeight) / 2

          if (i > 0) pdf.addPage()
          pdf.addImage(imageData, 'PNG', x, y, drawWidth, drawHeight, undefined, 'FAST')
        }

        const safeInvoiceNo = this.invoiceNumber.replace(/[^a-zA-Z0-9-_]/g, '_')
        pdf.save(`Invoice-${safeInvoiceNo}.pdf`)
      } finally {
        this.exportingPdf = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-view {
  --iv-primary: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%);
  --iv-bg: #eef3f8;
  --iv-paper: #ffffff;
  --iv-border: rgba(15, 23, 42, 0.12);
  --iv-border-strong: rgba(15, 23, 42, 0.18);
  --iv-head: rgba(6, 73, 184, 0.08);
  --iv-text: #0b1625;
  --iv-muted: #415064;
  --iv-accent: #0a7fd7;
  --iv-shadow: 0 18px 40px rgba(7, 32, 62, 0.14);

  background:
    radial-gradient(circle at 10% -5%, rgba(10, 127, 215, 0.14) 0%, transparent 42%),
    radial-gradient(circle at 90% 110%, rgba(3, 210, 197, 0.18) 0%, transparent 46%),
    var(--iv-bg);
  min-height: 100vh;
  padding: 116px 16px 32px;
  font-family: "Manrope", "Avenir Next", "Segoe UI Variable", "Noto Sans SC", "PingFang SC", sans-serif;
  color: var(--iv-text);
}

.invoice-loading {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 116px 16px 32px;
  box-sizing: border-box;
  background: #eef3f8;
  color: #415064;
  font-family: "Manrope", "Avenir Next", "Segoe UI Variable", "Noto Sans SC", "PingFang SC", sans-serif;
  font-size: 15px;
}

.invoice-loading .el-icon-loading {
  font-size: 22px;
  color: #0a7fd7;
}

.invoice-toolbar {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.invoice-content-layout {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
}

.invoice-content-layout--with-image {
  max-width: 1404px;
  display: grid;
  grid-template-columns: minmax(0, 1120px) 260px;
  gap: 24px;
  align-items: start;
}

.uploaded-invoice-preview {
  position: sticky;
  top: 110px;
  min-width: 0;
}

.uploaded-invoice-download {
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--iv-border);
  border-radius: 8px;
  background: var(--iv-paper);
  box-shadow: 0 12px 28px rgba(7, 32, 62, 0.12);
  color: var(--iv-text);
  cursor: pointer;
  text-align: left;
}

.uploaded-invoice-download:disabled {
  cursor: wait;
  opacity: 0.72;
}

.uploaded-invoice-image {
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  background: #f6f8fb;
}

.uploaded-invoice-meta {
  display: grid;
  gap: 7px;
  padding: 12px;
}

.uploaded-invoice-meta strong {
  overflow: hidden;
  font-size: 13px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uploaded-invoice-meta span {
  color: var(--iv-accent);
  font-size: 12px;
}

.uploaded-invoice-meta i {
  margin-right: 5px;
}

.export-btn {
  border: none;
  background: var(--iv-primary);
  color: #ffffff;
  font-size: 13px;
  letter-spacing: 0.02em;
  line-height: 1;
  padding: 11px 15px;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 10px 18px rgba(6, 73, 184, 0.26);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.export-btn--secondary {
  background: #ffffff;
  color: var(--iv-accent);
  border: 1px solid rgba(10, 127, 215, 0.24);
  box-shadow: 0 8px 16px rgba(7, 32, 62, 0.08);
}

.export-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.02);
  box-shadow: 0 12px 22px rgba(6, 73, 184, 0.32);
}

.export-btn:disabled {
  opacity: 0.68;
  cursor: not-allowed;
}

.invoice-page {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto 30px;
  background: var(--iv-paper);
  padding: 34px 34px 26px;
  border-radius: 14px;
  border: 1px solid rgba(11, 22, 37, 0.08);
  box-shadow: var(--iv-shadow);
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.invoice-page::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: var(--iv-primary);
}

.top-grid {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 2fr;
  gap: 28px;
  align-items: start;
}

.logo-wrap {
  margin-bottom: 14px;
}

.mt-logo {
  width: 252px;
  max-width: 100%;
  object-fit: contain;
}

.sender-box {
  border: 1px solid var(--iv-border);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.48;
  color: #111827;
  background: #fbfcff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}

.sender-box p {
  margin: 0;
}

.sender-box p + p {
  margin-top: 2px;
}

.meta-table,
.line-table {
  width: 100%;
  border: 1px solid var(--iv-border);
  border-radius: 10px;
  overflow: hidden;
}

.line-table {
  display: grid;
  grid-template-columns: 35fr 19fr 19fr 12fr 15fr;
  margin-top: 30px;
}

.meta-table {
  display: flex;
  flex-direction: column;
}

.meta-row {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
}

.line-row {
  display: contents;
}

.meta-cell,
.line-cell {
  border-bottom: 1px solid var(--iv-border);
  border-right: 1px solid var(--iv-border);
  padding: 9px 10px;
  font-size: 14px;
  color: var(--iv-text);
  vertical-align: top;
}

.meta-row:last-child .meta-cell,
.line-row--last .line-cell,
.line-cell--summary-spacer {
  border-bottom: none;
}

.meta-row .meta-cell:last-child,
.line-row .line-cell:last-child {
  border-right: none;
}

.meta-cell--full {
  grid-column: 1 / -1;
  border-right: none;
}

.line-cell--period,
.line-cell--summary-label {
  grid-column: span 2;
}

.line-cell--summary-spacer {
  grid-row: span 4;
}

.left-head,
.label-cell,
.head-row .line-cell,
.block-title {
  background: linear-gradient(134deg, rgba(6, 73, 184, 0.12) 1%, rgba(10, 127, 215, 0.12) 41%, rgba(3, 210, 197, 0.12) 99%);
}

.left-head,
.right-head {
  font-size: 26px !important;
  font-weight: 700;
  line-height: 1.16;
  letter-spacing: 0.01em;
}

.left-head {
  text-align: left;
}

.right-head,
.value-cell,
.amount-col,
.right {
  text-align: right;
}

.blank-row .meta-cell {
  height: 84px;
  background: #fff;
}

.matter-col {
  text-align: left;
}

.currency-col {
  text-align: center;
}

.amount-col {
  text-align: right;
}

.summary-label {
  text-align: left;
  color: #1f2937;
}

.strong {
  font-weight: 700;
}

.center {
  text-align: center;
}

.info-block {
  margin-top: 24px;
}

.block-title {
  width: 100%;
  border: 1px solid var(--iv-border);
  border-bottom: none;
  padding: 7px 10px;
  font-size: 15px;
  font-weight: 600;
  color: #102335;
  border-radius: 10px 10px 0 0;
}

.block-content {
  border: 1px solid var(--iv-border);
  padding: 9px 10px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iv-muted);
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  border-radius: 0px 0px 10px 10px;
}

.block-content p {
  margin: 0;
}

.bank-title-only .block-title {
  border-bottom: 1px solid var(--iv-border);
}

.footer-line {
  border-top: 1px solid rgba(15, 23, 42, 0.12);
  margin-top: 14px;
}

.invoice-payment-footer {
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--iv-border);
  break-inside: avoid;
  page-break-inside: avoid;
}

.invoice-payment-qr {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
  max-width: 620px;
  min-width: 0;
}

.invoice-payment-qr img {
  display: block;
  width: 244px;
  height: 244px;
  flex: 0 0 auto;
  background: #fff;
}

.invoice-payment-details {
  flex: 1;
  min-width: 0;
}

.invoice-payment-details h2 {
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 600;
}

.invoice-payment-details p {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  overflow-wrap: anywhere;
  color: var(--iv-muted);
}

.page-footer {
  margin-top: 12px;
  font-size: 12px;
  color: #425266;
  display: flex;
  justify-content: space-between;
}

.second-page {
  min-height: 980px;
}

.bank-box {
  border: 1px solid var(--iv-border);
  padding: 14px;
  font-size: 15px;
  line-height: 1.44;
  color: #101828;
  background: #fbfcff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}

.bank-box p {
  margin: 0;
}

.bank-box p + p {
  margin-top: 3px;
}

@media (max-width: 1200px) {
  .invoice-content-layout--with-image {
    max-width: 1120px;
    display: block;
  }

  .uploaded-invoice-preview {
    position: static;
    width: min(320px, 100%);
    margin: 0 0 24px auto;
  }

  .invoice-page {
    padding: 24px;
    border-radius: 12px;
  }

  .top-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}

@media (max-width: 768px) {
  .invoice-payment-footer {
    margin-top: 24px;
    padding-top: 20px;
  }

  .invoice-payment-qr {
    flex-direction: column;
    gap: 16px;
    max-width: 360px;
  }

  .invoice-payment-qr img {
    width: 244px;
    height: auto;
    max-width: 100%;
  }

  .invoice-payment-details {
    width: 100%;
  }

  .invoice-view {
    padding: 86px 8px 16px;
  }

  .invoice-toolbar {
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .uploaded-invoice-preview {
    width: 100%;
  }

  .invoice-page {
    padding: 14px;
    margin-bottom: 16px;
    border-radius: 8px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.09);
  }

  .mt-logo {
    width: 180px;
  }

  .sender-box,
  .meta-cell,
  .line-cell,
  .block-title,
  .block-content,
  .bank-box {
    font-size: 13px;
  }

  .left-head,
  .right-head {
    font-size: 15px !important;
  }

  .blank-row .meta-cell {
    height: 34px;
  }

  .block-title {
    width: 100%;
  }

  .page-footer {
    font-size: 11px;
    gap: 8px;
    flex-wrap: wrap;
  }

  .second-page {
    min-height: auto;
  }
}

@media print {
  .invoice-toolbar {
    display: none;
  }

  .invoice-view {
    background: #fff;
    padding: 0;
  }

  .invoice-page {
    margin: 0;
    max-width: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
    page-break-after: always;
  }
}
</style>
